import { CrateIdentity } from '@cratecc/identity-js';
import { ApiClient } from "./api.ts";
import { parseJwt, isTokenExpired } from "../utils/jwt.ts";
import { getToken, setToken, clearSession, logoutAndRedirect, SESSION_KEY } from "../utils/session.ts";
import md5 from "md5";

export interface User {
  id: string;
  email: string;
  name?: string;
  org_id?: string;
  plan_id?: string;
}

export class AuthService {
    private client: CrateIdentity;
    private static instance: AuthService;
    private api = ApiClient.getInstance();
    private isRegistering = false;

    private constructor() {
        const publishableKey = import.meta.env.CRATE_PUBLISHABLE_KEY || import.meta.env.PASSWORDLESS_PUBLIC_KEY;
        const domain = import.meta.env.API_URL || "https://api.crate.cx";

        if (!publishableKey) {
            console.warn('>>> AuthService: Missing CRATE_PUBLISHABLE_KEY');
        }

        console.debug('>>> AuthService: Initializing CrateIdentity with domain:', domain);
        this.client = new CrateIdentity({
            publishableKey: publishableKey || '',
            domain: domain,
        });

        // Clear profile cache whenever auth state changes
        window.addEventListener('auth-state-changed', () => {
            console.debug('>>> AuthService: Auth state changed, clearing profile cache');
            this.clearCache();
        });
    }

    public clearCache(): void {
        this.profilePromise = null;
        this.profileData = null;
    }


    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    public isAuthenticated(): boolean {
        const token = getToken();
        if (!token) return false;

        if (isTokenExpired(token)) {
            console.warn('>>> AuthService: Expired or malformed token found, removing...');
            this.logout();
            return false;
        }

        return true;
    }

    public logout(): void {
        logoutAndRedirect();
    }

    private parseJwt(token: string): any {
        return parseJwt(token);
    }

    public async checkAccount(email: string): Promise<{ exists: boolean }> {
        try {
            const response = await this.api.request<{ exists: boolean }>(`/auth/check?email=${encodeURIComponent(email)}`);
            return response as { exists: boolean };
        } catch (error: any) {
            if (error.message && (error.message.includes('403') || error.message.includes('closed beta'))) {
                throw error;
            }
            return { exists: false };
        }
    }

    public async signinWithAlias(alias: string): Promise<string> {
        try {
            // 1. Authenticate with Crate Identity
            const result = await this.client.login(alias);
            const identityToken = result.token || (result as any).data?.token;

            if (!identityToken) {
                throw new Error('No identity token received from Crate Identity');
            }

            // 2. Exchange Identity Token for Crate Session Token
            const data: any = await this.api.request(`/auth/passwordless`, {
                method: 'POST',
                body: JSON.stringify({ token: identityToken }),
            });

            const authToken = data.token || (data.data && data.data.token);
            if (authToken) {
                setToken(authToken);
                return authToken;
            }

            throw new Error('No session token received from backend exchange');
        } catch (error) {
            console.error('>>> AuthService: Sign-in failed:', error);
            throw error;
        }
    }

    public async triggerEmailVerification(email: string, name: string = 'User'): Promise<void> {
        await this.api.request(`/auth/email`, {
            method: 'POST',
            body: JSON.stringify({ email, name }),
        });
    }

    public async completeRegistration(verificationToken: string): Promise<string> {
        if (this.isRegistering) {
            console.warn('>>> AuthService: Registration already in progress, skipping...');
            return '';
        }

        try {
            this.isRegistering = true;
            // Clear any existing session to prevent header collisions during registration
            this.logout();

            // 1. Exchange Magic Link Token for Crate Identity Registration Token
            const data: any = await this.api.request(`/auth/passwordless?provider=crate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${verificationToken}`,
                }
            });

            console.debug('>>> AuthService: Received registration data:', data);
            const registrationToken = data.token || (data.data && data.data.token);
            
            if (!registrationToken) {
                throw new Error('Failed to obtain Crate Identity Registration Token');
            }

            // 2. Register passkey with Crate Identity
            const result = await this.client.register(registrationToken);
            const identityToken = result.token || (result as any).data?.token;

            if (!identityToken) {
                throw new Error('Passkey registered, but no identity token was returned');
            }

            // 3. Exchange new Identity Token for Crate Session Token
            const exchangeData: any = await this.api.request(`/auth/passwordless`, {
                method: 'PUT',
                body: JSON.stringify({ token: identityToken }),
            });

            const authToken = exchangeData.token || (exchangeData.data && exchangeData.data.token);
            
            if (authToken) {
                setToken(authToken);

                // If this was an invitation (has 'itk' claim), automatically accept it
                const payload = this.parseJwt(verificationToken);
                if (payload && payload.itk) {
                    console.debug('>>> AuthService: Invitation detected, accepting...', payload.itk);
                    try {
                        const { OrganizationInvitesService } = await import("./organization-invites.ts");
                        await OrganizationInvitesService.getInstance().acceptInvite(payload.itk);
                        console.debug('>>> AuthService: Invitation accepted successfully');
                    } catch (e) {
                        console.error('>>> AuthService: Failed to automatically accept invitation:', e);
                    }
                }

                return authToken;
            }

            throw new Error('No session token received from backend after registration');
        } catch (error) {
            console.error('Registration/Auto-login failed:', error);
            throw error;
        } finally {
            this.isRegistering = false;
        }
    }

    private profilePromise: Promise<any> | null = null;
    private profileData: any = null;

    public async getProfile(force = false): Promise<any> {
        if (this.profileData && !force) {
            return this.profileData;
        }

        if (this.profilePromise) {
            return this.profilePromise;
        }

        this.profilePromise = (async () => {
            try {
                const profile: any = await this.api.request('/user/profile');
                if (profile && profile.email && !profile.gravatar) {
                    profile.gravatar = md5(profile.email.trim().toLowerCase());
                }

                // Sync organization context to localStorage for ApiClient
                if (profile && profile.org_id) {
                    localStorage.setItem('crate_org_id', profile.org_id);
                } else {
                    console.debug('>>> AuthService: No organization ID in profile, clearing context');
                    localStorage.removeItem('crate_org_id');
                }

                this.profileData = profile;
                return profile;
            } catch (error: any) {
                // Logout and reload if authenticated request fails with 401 (Unauthorized), 403 (Forbidden), or 404 (Not Found)
                if (error.status === 401 || error.status === 403 || error.status === 404) {
                    console.warn(`>>> AuthService: Profile fetch failed with status ${error.status}, logging out...`);
                    logoutAndRedirect();
                    return;
                }
                throw error;
            } finally {
                this.profilePromise = null;
            }
        })();

        return this.profilePromise;
    }

    async updateProfile(details: { displayName?: string; image?: string }): Promise<any> {
        const body = {
            display_name: details.displayName,
            image: details.image,
        };
        return this.api.request('/user/profile', {
            method: 'PATCH',
            body: JSON.stringify(body)
        });
    }

    public async switchOrganization(orgId: string): Promise<any> {
        console.debug(`>>> AuthService: Switching to organization ${orgId}`);
        // Clear cache BEFORE the request to ensure next getProfile() fetch fresh data
        this.clearCache();
        
        let switchResolver: () => void;
        const switchPromise = new Promise<void>((resolve) => { switchResolver = resolve; });
        (this.api as any).setSessionSwitchPromise(switchPromise);
        
        let profile: any;
        try {
            const response: any = await this.api.request('/user/session', {
                method: 'POST',
                body: JSON.stringify({ org_id: orgId }),
                toast: { success: 'Organization switched' }
            });
            
            // Use either response.data (standard) or response directly
            profile = response.data || response;

            if (profile && profile.org_id) {
                localStorage.setItem('crate_org_id', profile.org_id);
                this.profileData = profile;
                
                // If the response includes a refreshed token, update it
                if (profile.token) {
                    setToken(profile.token);
                }
            }
        } finally {
            (this.api as any).setSessionSwitchPromise(null);
            switchResolver!();
        }

        // Fetch fresh profile if needed, after the lock is released to avoid deadlock
        if (!profile || !profile.org_id) {
            console.debug('>>> AuthService: Session switched, fetching fresh profile...');
            profile = await this.getProfile(true);
        }
        
        window.dispatchEvent(new CustomEvent('auth-state-changed', { 
            detail: { type: 'org-switch', orgId: localStorage.getItem('crate_org_id') } 
        }));

        return profile;
    }

    public getUser(): User | null {
        const token = getToken();
        if (!token) return null;
        const payload = parseJwt(token);
        if (!payload) return null;
        
        return {
            id: payload.sub || payload.uid,
            email: payload.email,
            org_id: payload.org_id || payload.orgId || localStorage.getItem('crate_org_id') || undefined,
            plan_id: payload.plan_id || payload.planId
        };
    }

    // Mock for compatibility with onboarding mock
    public updateUser(updates: Partial<User>) {
        console.log('AuthService.updateUser mock called with:', updates);
    }
}

export const authService = AuthService.getInstance();
